import React, { useState } from 'react';
import { Box, IconButton, Modal, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import {
    Color,
    ObjectGroupForm,
    ObjectGroupFormData,
    ObjectGroupType,
    ConfirmWarningModal,
} from '@europrocurement/flexy-components';
import { enqueueSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { useFormaliteService } from '@europrocurement/l2d-domain';
import { AnyAction } from '@reduxjs/toolkit';
import {
    useDossierGroupeAction,
    useDossierGroupeActionDispatch,
} from '@europrocurement/l2d-modules/src/modules/Dossiers/providers/DossierGroupeActionProvider';

import { useDossierGroup } from '@europrocurement/l2d-modules/src/modules/Dossiers/hooks';

export const ModalDossierGroupAction = function () {
    const { action, selectedDossierGroupe, modalOpen } = useDossierGroupeAction();
    const dispatch = useDispatch();
    const dispatchModal = useDossierGroupeActionDispatch();
    const { getDossierOpenSearchObjectGroups } = useFormaliteService();
    const { palette } = useTheme();
    const { editDossierGroup, deleteDossierGroup } = useDossierGroup();
    const [submitingProcess, setSubmitingProcess] = useState<boolean>(false);

    const handleCloseModal = () => {
        setSubmitingProcess(false);
        dispatchModal({ type: 'close_and_reset' });
    };

    const handleEdit = async (group: ObjectGroupFormData) => {
        if (selectedDossierGroupe) {
            const groupPayload = {
                label: group.namegroup,
                color: group.color,
                id: selectedDossierGroupe.id,
            };

            try {
                await editDossierGroup(groupPayload as ObjectGroupType);
                enqueueSnackbar('Votre groupe a bien été édité');
                dispatch(getDossierOpenSearchObjectGroups({}) as unknown as AnyAction);
                handleCloseModal();
            } catch (err) {
                console.error(err);
                enqueueSnackbar("Une erreur est survenue durant l'édition de votre groupe", {
                    variant: 'error',
                });
            }
        } else {
            enqueueSnackbar(
                "Une erreur est survenue durant l'édition de votre groupe, groupe non trouvé",
                {
                    variant: 'error',
                },
            );
        }
    };

    const handleDelete = async () => {
        if (selectedDossierGroupe) {
            setSubmitingProcess(true);
            const groupPayload = {
                id: selectedDossierGroupe.id,
            };
            try {
                await deleteDossierGroup(groupPayload as ObjectGroupType);
                enqueueSnackbar('Votre groupe a bien été supprimé');
                dispatch(getDossierOpenSearchObjectGroups({}) as unknown as AnyAction);
                handleCloseModal();
                setSubmitingProcess(false);
            } catch (err) {
                console.error(err);
                enqueueSnackbar('Une erreur est survenue durant la suppression de votre groupe', {
                    variant: 'error',
                });
                setSubmitingProcess(false);
            }
        } else {
            enqueueSnackbar(
                'Une erreur est survenue durant la suppression de votre groupe, groupe non trouvé',
                {
                    variant: 'error',
                },
            );
        }
    };

    const dossierGroupeColors: Color[] = [
        'blue',
        'red',
        'danger',
        'warning',
        'success',
        'deepPurple',
        'pink',
    ];

    if (action === 'edit') {
        return (
            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                aria-labelledby="create-modal-title"
                aria-describedby="create-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 500,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: '12px',
                    }}
                >
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseModal}
                        sx={{
                            fontSize: '16px',
                            position: 'absolute',
                            right: '20px',
                            top: '35px',
                            color: palette.grey[500],
                        }}
                    >
                        <FaOptionIcon icon={['fas', 'close']} />
                    </IconButton>
                    <ObjectGroupForm
                        title="Editer un groupe de dossiers"
                        fieldLabel="Nom"
                        submitButtonLabel="Modifier"
                        onSubmit={handleEdit}
                        onCancel={handleCloseModal}
                        colors={dossierGroupeColors}
                        nameValue={selectedDossierGroupe?.label}
                        colorValue={selectedDossierGroupe?.color as Color}
                    />
                </Box>
            </Modal>
        );
    }
    if (action === 'delete') {
        return (
            <ConfirmWarningModal
                actionOnValidation={handleDelete}
                handleCloseModal={handleCloseModal}
                modalOpen={modalOpen}
                submitingProcess={submitingProcess}
                title={`Supprimer ${selectedDossierGroupe?.label} ?`}
                message="Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est définitive"
                validationButtonText="Oui, supprimer"
            />
        );
    }
    return null;
};
